//src/pages/questions/AssessmentDesignerPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  fetchRandomizedQuestions,
  saveAssessment,
} from "../../api/questions/questionsApi";
import { IoMdArrowRoundBack } from "react-icons/io";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { FaEdit, FaLock } from "react-icons/fa";

const AssessmentDesignerPage = () => {
  const { projectId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    difficulty,
    testType,
    sections,
    questionCounts,
    totalTime,
    sectionMarks,
    marksPerQuestion,
  } = state;
  //const totalMarks = Object.values(sectionMarks).reduce((sum, marks) => sum + marks, 0);
  const [subjectiveQuestions, setSubjectiveQuestions] = useState([]);
  const [objectiveQuestions, setObjectiveQuestions] = useState([]);
  const [suggestedSubjectiveQuestions, setSuggestedSubjectiveQuestions] =
    useState([]);
  const [suggestedObjectiveQuestions, setSuggestedObjectiveQuestions] =
    useState([]);
  const [anchorQuestion, setAnchorQuestion] = useState("");
  const [fetchedSessionMarks, setFetchedSessionMarks] = useState({
    conversational: 0,
    subjective: 0,
    objective: 0,
  });
  const [subjectiveTotalMarks, setSubjectiveTotalMarks] = useState("");
  const [objectiveTotalMarks, setObjectiveTotalMarks] = useState("");
  const [fetchedQuestionCount, setFetchedQuestionCount] = useState({
    conversational: 0,
    subjective: 0,
    objective: 0,
  });
  const [isSubjectiveEdit, setIsSubjectiveEdit] = useState(false);
  const [isObjectiveEdit, setIsObjectiveEdit] = useState(false);

  useEffect(() => {
    const STM = fetchedQuestionCount.subjective * marksPerQuestion.subjective;
    const OTM = fetchedQuestionCount.objective * marksPerQuestion.objective;
    setSubjectiveTotalMarks(STM);
    setObjectiveTotalMarks(OTM);
    // console.log(subjectiveTotalMarks);
    // console.log(objectiveTotalMarks);
    // console.log(fetchedSessionMarks);
    setFetchedSessionMarks({
      conversational: sectionMarks.conversational,
      subjective: STM,
      objective: OTM,
      // Added grand total to session marks
    });
  }, [fetchedQuestionCount, marksPerQuestion, sectionMarks]);

  const backButtonHandler = () => {
    navigate(`/create-assessment/step1/${projectId}`);
  };

  const grandTotalMarks =
    (sectionMarks?.conversational || 0) +
    (subjectiveTotalMarks || 0) +
    (objectiveTotalMarks || 0);

  // Fetch questions initially
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const data = await fetchRandomizedQuestions(
          projectId,
          difficulty,
          questionCounts.subjective,
          questionCounts.objective,
          sections
        );
        setFetchedQuestionCount({
          subjective: data.subjectiveQuestions.length,
          objective: data.objectiveQuestions.length,
          conversational: questionCounts.conversational,
        });

        setSubjectiveQuestions(
          Array.isArray(data.subjectiveQuestions)
            ? data.subjectiveQuestions
            : []
        );
        setObjectiveQuestions(
          Array.isArray(data.objectiveQuestions) ? data.objectiveQuestions : []
        );
        setSuggestedSubjectiveQuestions(
          Array.isArray(data.suggestedSubjectiveQuestions)
            ? data.suggestedSubjectiveQuestions
            : []
        );
        setSuggestedObjectiveQuestions(
          Array.isArray(data.suggestedObjectiveQuestions)
            ? data.suggestedObjectiveQuestions
            : []
        );
      } catch (error) {
        console.error("Error fetching questions:", error.message);
        alert("Failed to fetch questions. Please try again.");
      }
    };

    fetchQuestions();
  }, [projectId, difficulty, questionCounts, sections]);


  const handleDragStart = (e, question, isSubjective, index) => {
    e.dataTransfer.setData("question", JSON.stringify(question));
    e.dataTransfer.setData("isSubjective", isSubjective);
    e.dataTransfer.setData("suggestedIndex", index);
  };

  const handleDrop = (e, index, isSubjective) => {
    const droppedQuestion = JSON.parse(e.dataTransfer.getData("question"));
    const isDroppedSubjective =
      e.dataTransfer.getData("isSubjective") === "true";
    const suggestedIndex = parseInt(
      e.dataTransfer.getData("suggestedIndex"),
      10
    );

    if (isSubjective && isDroppedSubjective) {
      const updatedQuestions = [...subjectiveQuestions];
      const replacedQuestion = updatedQuestions[index];

      const updatedSuggestedQuestions = [...suggestedSubjectiveQuestions];
      updatedSuggestedQuestions[suggestedIndex] = replacedQuestion;

      updatedQuestions[index] = droppedQuestion;
      setSubjectiveQuestions(updatedQuestions);
      setSuggestedSubjectiveQuestions(updatedSuggestedQuestions);
    } else if (!isSubjective && !isDroppedSubjective) {
      const updatedQuestions = [...objectiveQuestions];
      const replacedQuestion = updatedQuestions[index];

      const updatedSuggestedQuestions = [...suggestedObjectiveQuestions];
      updatedSuggestedQuestions[suggestedIndex] = replacedQuestion;

      updatedQuestions[index] = droppedQuestion;
      setObjectiveQuestions(updatedQuestions);
      setSuggestedObjectiveQuestions(updatedSuggestedQuestions);
    }
  };

  
  const handleTextChange = (e, index, isSubjective) => {
    if (isSubjective) {
      setSubjectiveQuestions((prevQuestions) => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[index] = {
          ...updatedQuestions[index],
          question_text: e.target.value,
        };
        return updatedQuestions;
      });
    } else {
      setObjectiveQuestions((prevQuestions) => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[index] = {
          ...updatedQuestions[index],
          question_text: e.target.value,
        };
        return updatedQuestions;
      });
    }
  };
  const handleOptionChange = (e, questionIndex, optionIndex) => {
    const updatedQuestions = [...objectiveQuestions];
    updatedQuestions[questionIndex].options[optionIndex] = e.target.value;
    setObjectiveQuestions(updatedQuestions);
  };

  const handleSubmit = async () => {
    if (sections.conversational_ai && !anchorQuestion.trim()) {
      alert(
        "Please enter a valid anchor question for the Conversational AI section."
      );
      return;
    }
    try {
      const v = false;
      await saveAssessment(
        projectId,
        difficulty,
        testType,
        sections,
        fetchedQuestionCount,
        totalTime,
        subjectiveQuestions,
        objectiveQuestions,
        anchorQuestion,
        fetchedSessionMarks,
        marksPerQuestion,
        v
      );
      alert("Assessment saved successfully!");
      navigate(`/projects`);
    } catch (error) {
      console.error("Error saving assessment:", error.message);
      alert("Failed to save assessment. Please try again.");
    }
  };

  return (
    <div className=" w-full pb-10">
      <div className="flex items-center justify-between lg:w-7/12 md:w-10/12 w-11/12 mx-auto py-4">
       <button
                      onClick={backButtonHandler}
                      className="group flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-md hover:scale-105 active:scale-95 transition"
                    >
                      <IoMdArrowRoundBack className="text-xl group-hover:-translate-x-1 transition" />
                      <span className="hidden md:block">Back</span>
                    </button>
        <h2 className="text-xl font-bold text-center hidden md:block text-[#36369f]">
          Review and Edit Questions
        </h2>
        <div className="font-semibold">
          Total Marks:{" "}
          <span className="font-extrabold text-red-500">
            {" "}
            {grandTotalMarks}{" "}
          </span>
        </div>
      </div>
      <h2 className="pb-4 font-bold text-center block md:hidden text-[#36369f]">
        Review and Edit Questions
      </h2>
      <div className="w-11/12 mx-auto flex flex-col justify-items-center">
        {(questionCounts?.objective > objectiveQuestions.length ||
          questionCounts?.subjective > subjectiveQuestions.length) && (
            <div className="bg-red-100 border border-red-300 text-red-600 rounded-xl p-4 text-sm mb-2">
            <p className="font-bold text-red-500">
              You haven’t uploaded sufficient learning materials to generate the
              desired number of{" "}
              {questionCounts.subjective > 0 &&
                `${questionCounts.subjective} subjective`}
              {questionCounts.subjective > 0 &&
                questionCounts.objective > 0 &&
                " and "}
              {questionCounts.objective > 0 &&
                `${questionCounts.objective} objective`}{" "}
              questions.
            </p>
          </div>
        )}

        <div className=" w-full flex md:flex-row flex-col-reverse lg:gap-10 gap-y-4 md:gap-y-0 md:gap-3">
          
          {sections.subjective && (
            <div className="md:w-8/12 w-full mx-auto">
              <div className="flex justify-between items-center bg-[#36369f] text-white md:px-8 px-3 py-3 rounded-t-xl">
                <div className="flex items-center gap-[5px]">
                  <h3 className="md:text-base text-sm font-bold">
                    Subjective Questions
                  </h3>
                  <button
                    onClick={() => setIsSubjectiveEdit(!isSubjectiveEdit)}
                    className="text-purple-600 "
                  >
                    {isSubjectiveEdit ? (
                      <div className="bg-white p-1 flex items-center gap-2 rounded-lg">
                        <span>Lock</span>
                        <FaLock className="md:text-base text-sm" />
                      </div>
                    ) : (
                      <div className="bg-white p-1 flex items-center gap-2 rounded-lg">
                        <span>Edit</span>
                        <FaEdit className="md:text-base text-sm" />
                      </div>
                    )}
                  </button>
                </div>
                <p className="md:text-base text-sm ">
                  Total Marks: {subjectiveTotalMarks}
                </p>
              </div>
              <div
                className="w-full items-center flex flex-col justify-center"
                style={{
                  boxShadow:
                    "-2px -2px 15px 0px rgba(0, 0, 0, 0.25) inset, 2px 0px 15px 0px rgba(0, 0, 0, 0.25) inset",
                }}
              >
                {subjectiveQuestions.map((question, index) => (
                  <div
                    key={question.question_id}
                    className=" w-full mx-auto lg:py-5 py-2 flex border-b-2 lg:px-10 md:px-4 px-1 lg:gap-4 gap-2"
                    onDrop={(e) => handleDrop(e, index, true)}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <p className="py-2 font-semibold lg:text-base md:text-sm text-xs">
                      Q{index + 1}.
                    </p>
                    <input
                      className="lg:w-9/12 md:w-10/12 w-9/12 md:py-2 lg:text-base md:text-sm text-xs font-bold bg-transparent"
                      defaultValue={question.question_text}
                      onChange={(e) => handleTextChange(e, index, true)}
                      readOnly={!isSubjectiveEdit}
                      style={{
                        cursor: isSubjectiveEdit ? "text" : "default",
                        backgroundColor: isSubjectiveEdit
                          ? "#f3f4f6"
                          : "transparent",
                      }}
                    />
                    <p className="lg:w-3/12 md:w-2/12 w-3/12 lg:text-base md:text-sm text-xs text-right mx-auto py-2">
                      [{marksPerQuestion.subjective} marks]
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          
          {sections.subjective && (
            <div className="md:w-4/12 w-full mx-auto ">
              <h3 className=" bg-[#36369f] lg:py-2 py-1 lg:px-6 px-2 lg:text-base text-sm text-white font-semibold rounded-t-xl mb-2">
                Additional suggested questions (drag and drop these questions
                into the question boxes on the left):
              </h3>
              <div className="flex flex-col lg:gap-y-3 gap-y-1">
                {suggestedSubjectiveQuestions.map(
                  (question, suggestedIndex) => (
                    <div
                      className=" text-sm w-full rounded-lg mx-auto shadow-lg lg:py-4 py-2 lg:px-4 px-2"
                      style={{
                        boxShadow: "1px 1px 10px 0px rgba(0, 0, 0, 0.25)",
                      }}
                      key={question.question_id}
                      draggable
                      onDragStart={(e) =>
                        handleDragStart(e, question, true, suggestedIndex)
                      }
                    >
                      <p className=""> {question.question_text}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        <div className=" w-full flex md:flex-row flex-col-reverse lg:gap-10 gap-3 pt-32 ">
          
          {sections.objective && (
            <div className="md:w-8/12 w-full">
              <div className="flex justify-between items-center bg-[#36369f] text-white lg:px-8 px-4 py-3 rounded-t-xl">
                <div className="flex items-center gap-[5px]">
                  <h3 className="lg:text-base text-sm font-bold">
                    Objective Questions
                  </h3>
                  <button
                    onClick={() => setIsObjectiveEdit(!isObjectiveEdit)}
                    className="text-purple-600"
                  >
                    {isObjectiveEdit ? (
                      <div className="bg-white p-1 flex items-center gap-2 rounded-lg">
                      <span>Lock</span>
                      <FaLock className="md:text-base text-sm" />
                    </div>
                  ) : (
                    <div className="bg-white p-1 flex items-center gap-2 rounded-lg">
                      <span>Edit</span>
                      <FaEdit className="md:text-base text-sm" />
                    </div>
                    )}
                  </button>
                </div>
                <p className="lg:text-base text-sm">
                  Total Marks: {objectiveTotalMarks}
                </p>
              </div>
              <div
                className="w-full items-center flex flex-col justify-center"
                style={{
                  boxShadow:
                    "-2px -2px 15px 0px rgba(0, 0, 0, 0.25) inset, 2px 0px 15px 0px rgba(0, 0, 0, 0.25) inset",
                }}
              >
                {objectiveQuestions.map((question, index) => (
                  <div
                    key={question.question_id}
                    className=" w-full mx-auto py-3 border-b-2 lg:px-10 md:px-4 px-1"
                    onDrop={(e) => handleDrop(e, index, false)}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center gap-2">
                      <p className="lg:text-base md:text-sm text-xs">
                        Q{index + 1}.
                      </p>
                      <textarea
                        className="w-[80%]  font-semibold lg:text-base md:text-sm text-xs"
                        defaultValue={question.question_text}
                        onChange={(e) => handleTextChange(e, index, false)}
                        readOnly={!isObjectiveEdit}
                        style={{
                          cursor: isObjectiveEdit ? "text" : "default",
                          backgroundColor: isObjectiveEdit
                            ? "#f3f4f6"
                            : "transparent",
                        }}
                      />
                      <p className="w-[20%] text-right lg:text-base md:text-sm text-xs">
                        [{marksPerQuestion.objective} marks]
                      </p>
                    </div>

                    <div className="w-full mx-auto grid grid-cols-2 gap-y-2 lg:gap-x-6 gap-x-2 lg:px-8 md:px-3 px-1">
                      {question.options.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          className="flex items-center lg:gap-2 lg:px-4 px-2 bg-[#B1C2F4] rounded-lg"
                        >
                          <label className="font-bold lg:text-base md:text-sm text-xs">
                            {" "}
                            {String.fromCharCode(97 + optIndex).toUpperCase()}.
                          </label>
                          <input
                            className="md:text-sm text-xs lg:text-center p-2 w-full font-semibold bg-transparent"
                            key={optIndex}
                            type="text"
                            value={option}
                            onChange={(e) =>
                              handleOptionChange(e, index, optIndex)
                            }
                            readOnly={!isObjectiveEdit}
                            style={{
                              cursor: isObjectiveEdit ? "text" : "default",
                              backgroundColor: isObjectiveEdit
                                ? "rgba(78,220,116,0.20)"
                                : "transparent",
                            }}
                          />
                        </div>
                      ))}
                      {/* {question.answer && (
                            <div className="w-full mt-2 font-semibold">
                              <label>Answer:</label><input classname='w-1/5 mx-2' type="text" value={question.answer}/>
                            </div>
                      )} */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          
          {sections.objective && (
            <div className="md:w-4/12 w-full">
              <h3 className="bg-[#36369f] py-2 lg:px-6 px-2 lg:text-base text-sm text-white font-semibold rounded-t-xl mb-2">
                Additional suggested questions (drag and drop these questions
                into the question boxes on the left):
              </h3>
              <div className="flex flex-col gap-y-3">
                {suggestedObjectiveQuestions.map((question, suggestedIndex) => (
                  <div
                    key={question.question_id}
                    className=" text-sm w-full rounded-lg mx-auto shadow-lg lg:py-4 py-2 lg:px-4 px-2"
                    style={{
                      boxShadow: "1px 1px 10px 0px rgba(0, 0, 0, 0.25)",
                    }}
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, question, false, suggestedIndex)
                    }
                  >
                    <p className="">{question.question_text}</p>

                    <div className="w-full grid grid-cols-2 lg:gap-4 gap-2 pt-2">
                      {question.options.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          className="flex items-center gap-1 lg:gap-0 lg:px-4 px-1 py-1 bg-[rgba(78,220,116,0.60)] rounded-lg overflow-hidden"
                        >
                          <label className="text-sm">
                            {" "}
                            {String.fromCharCode(97 + optIndex).toUpperCase()}.
                          </label>
                          <input
                            className="bg-transparent w-full text-xs lg:text-center font-semibold"
                            key={optIndex}
                            type="text"
                            value={option}
                            readOnly
                          />
                        </div>
                      ))}
                      {/* {question.answer && (
                                <div className="w-full my-2 text-blue-700 font-semibold">
                                    <p>Answer: {question.answer}</p>
                                </div>
                            )} */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        
        <div className=" w-full pt-32 pb-16">
          {sections.conversational_ai && (
            <div>
              <div className="flex justify-between items-center bg-[#36369f] text-white lg:px-8 px-4 py-3 rounded-t-xl">
                <h3 className="lg:w-[70%] w-[55%] md:text-base text-sm">
                  Conversational AI Questions
                </h3>
                <p className="lg:w-[15%] w-[20%] md:text-base text-sm font-bold">
                  Total Marks: {sectionMarks.conversational}
                </p>
                <p className="lg:w-[15%] w-[25%] md:text-base text-sm">
                  Marks Per Question: {marksPerQuestion.conversational}
                </p>
              </div>
              <p className="text-center py-2 font-bold md:text-base text-sm">
                These questions will be dynamically generated during the
                assessment phase.
              </p>

              <div className="border-2 bg-[rgba(177,194,244,0.50)] rounded-lg py-4 lg:px-16 px-4">
                <p className="lg:text-base text-sm font-semibold pb-1 pl-1">
                  Enter Your Anchor Question
                </p>
                <input
                  className="w-full p-4 rounded-lg lg:text-base text-sm"
                  style={{
                    boxShadow:
                      "1px -1px 10px 0px #B1C2F4 inset, -1px 3px 9px 0px #C8DBED inset",
                  }}
                  type="text"
                  placeholder="Enter anchor question"
                  value={anchorQuestion}
                  onChange={(e) => setAnchorQuestion(e.target.value)}
                />
                <div className="flex items-center md:justify-start justify-between gap-2">
                  <BsFillInfoCircleFill
                    aria-label="information icon"
                    className="text-4xl md:text-base"
                  />
                  <p className="my-5 md:text-base text-sm">
                    This question will serve as the first question in this
                    section, that will initiate the AI-driven conversation.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className=" mx-auto">
          <button
            className="bg-gradient-to-r from-purple-600 to-pink-500 px-20 py-2 rounded-lg tracking-tight text-white"
            onClick={handleSubmit}
          >
            Create Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDesignerPage;
